import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface MenuSubsection {
  title: string;
  collections: string[];
}

interface MenuSection {
  id: string;
  title: string;
  subsections: MenuSubsection[];
  directCollectionHandle?: string;
  imageUrl?: string;
}

interface Collection {
  node: {
    id: string;
    title: string;
    handle: string;
  };
}

interface CategoryGroup {
  id: string;
  name: string;
  collectionHandles: string[];
}

interface CategoryFilterProps {
  collections: Collection[];
  selectedCategories: string[];
  currentCategory: string | undefined;
  onToggleCategory: (handle: string) => void;
  openCategories?: string[]; // Categories to show as open/ungrouped from admin settings
}

const CategoryFilter = ({
  collections,
  selectedCategories,
  currentCategory,
  onToggleCategory,
  openCategories = [],
}: CategoryFilterProps) => {
  const [groups, setGroups] = useState<CategoryGroup[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const groupRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    loadMenuSections();
  }, [currentCategory]);

  // Auto-scroll to active group after loading
  useEffect(() => {
    if (!isLoading && activeGroupId) {
      setTimeout(() => {
        const groupElement = groupRefs.current.get(activeGroupId);
        if (groupElement) {
          groupElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [isLoading, activeGroupId]);

  const loadMenuSections = async () => {
    try {
      // Load header_collections from site_settings (navigation menu structure)
      const { data: settingsData } = await supabase
        .from("site_settings")
        .select("setting_value")
        .eq("setting_key", "header_collections")
        .single();

      if (settingsData?.setting_value) {
        // The structure is { sections: [...] } not a direct array
        const settingValue = settingsData.setting_value as unknown as { sections?: MenuSection[] };
        const menuSections = settingValue.sections || [];
        
        // Transform menu sections into category groups
        // Each section becomes a group, all collections from its subsections become group items
        const categoryGroups: CategoryGroup[] = menuSections
          .filter(section => !section.directCollectionHandle) // Exclude sections with direct links
          .map(section => {
            // Collect all collection handles from all subsections
            const allCollectionHandles: string[] = [];
            section.subsections?.forEach(subsection => {
              subsection.collections?.forEach(handle => {
                if (!allCollectionHandles.includes(handle)) {
                  allCollectionHandles.push(handle);
                }
              });
            });
            
            return {
              id: section.id,
              name: section.title,
              collectionHandles: allCollectionHandles
            };
          })
          .filter(group => group.collectionHandles.length > 0); // Only keep groups with collections

        setGroups(categoryGroups);

        // Expand groups that have selected items and find active group for scrolling
        const groupsToExpand = new Set<string>();
        let foundActiveGroup: string | null = null;
        
        categoryGroups.forEach(group => {
          group.collectionHandles.forEach(handle => {
            if (selectedCategories.includes(handle) || currentCategory === handle) {
              groupsToExpand.add(group.id);
              if (currentCategory === handle) {
                foundActiveGroup = group.id;
              }
            }
          });
        });
        
        setExpandedGroups(groupsToExpand);
        setActiveGroupId(foundActiveGroup);
      }
    } catch (error) {
      console.error("Error loading menu sections:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getCollectionByHandle = (handle: string) => {
    return collections.find((c) => c.node.handle === handle);
  };

  const toggleGroupExpanded = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const isSelected = (handle: string) => {
    return selectedCategories.includes(handle) || handle === currentCategory;
  };

  const getGroupSelectedCount = (group: CategoryGroup) => {
    return group.collectionHandles.filter(handle => isSelected(handle)).length;
  };

  // Get all handles that are in groups
  const getGroupedHandles = (): Set<string> => {
    const handles = new Set<string>();
    groups.forEach(group => {
      group.collectionHandles.forEach(handle => handles.add(handle));
    });
    return handles;
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-10 bg-muted animate-pulse rounded" />
        ))}
      </div>
    );
  }

  // If no groups defined, show flat list
  if (groups.length === 0) {
    return (
      <div className="space-y-2">
        {collections.map((collection) => (
          <label
            key={collection.node.id}
            className={cn(
              "flex items-center gap-3 w-full text-right px-3 py-2 rounded transition-colors cursor-pointer",
              isSelected(collection.node.handle)
                ? "bg-primary/10 text-primary"
                : "hover:bg-muted text-foreground"
            )}
          >
            <input
              type="checkbox"
              checked={isSelected(collection.node.handle)}
              onChange={() => onToggleCategory(collection.node.handle)}
              className="accent-primary w-4 h-4"
            />
            <span
              style={{
                fontWeight: isSelected(collection.node.handle) ? 400 : 300,
                fontSize: "14px",
              }}
            >
              {collection.node.title}
            </span>
          </label>
        ))}
      </div>
    );
  }

  const groupedHandles = getGroupedHandles();

  // Get open categories that exist in collections but not in groups
  const openCategoryCollections = openCategories
    .filter(handle => {
      return !groupedHandles.has(handle) && collections.some(c => c.node.handle === handle);
    })
    .map(handle => collections.find(c => c.node.handle === handle)!)
    .filter(Boolean);

  return (
    <div className="space-y-1">
      {/* Grouped categories */}
      {groups.map((group, index) => {
        const selectedCount = getGroupSelectedCount(group);
        
        // Only show groups that have items
        if (group.collectionHandles.length === 0) return null;

        return (
          <div key={group.id} ref={(el) => { if (el) groupRefs.current.set(group.id, el); }}>
            {/* Separator between groups */}
            {index > 0 && (
              <div className="h-px bg-border/30 my-2" />
            )}
            <Collapsible
              open={expandedGroups.has(group.id)}
              onOpenChange={() => toggleGroupExpanded(group.id)}
            >
              <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2.5 rounded hover:bg-muted/30 transition-colors">
                <span
                  className={cn(
                    "text-sm",
                    selectedCount > 0 ? "text-primary font-medium" : "text-foreground"
                  )}
                  style={{ fontWeight: selectedCount > 0 ? 500 : 400 }}
                >
                  {group.name}
                  {selectedCount > 0 && (
                    <span className="mr-1 text-xs text-primary/70">
                      ({selectedCount})
                    </span>
                  )}
                </span>
                <ChevronDown
                  className={cn(
                    "h-4 w-4 transition-transform duration-200 text-muted-foreground",
                    expandedGroups.has(group.id) && "rotate-180"
                  )}
                />
              </CollapsibleTrigger>
              <CollapsibleContent className="pr-3 space-y-1 pb-2 pt-1">
                {group.collectionHandles.map((handle) => {
                  const collection = getCollectionByHandle(handle);
                  if (!collection) return null;

                  return (
                    <label
                      key={handle}
                      className={cn(
                        "flex items-center gap-3 w-full text-right px-3 py-1.5 rounded transition-colors cursor-pointer",
                        isSelected(collection.node.handle)
                          ? "bg-primary/10 text-primary"
                          : "hover:bg-muted text-foreground"
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected(collection.node.handle)}
                        onChange={() => onToggleCategory(collection.node.handle)}
                        className="accent-primary w-4 h-4"
                      />
                      <span
                        style={{
                          fontWeight: isSelected(collection.node.handle) ? 400 : 300,
                          fontSize: "13px",
                        }}
                      >
                        {collection.node.title}
                      </span>
                    </label>
                  );
                })}
              </CollapsibleContent>
            </Collapsible>
          </div>
        );
      })}

      {/* Open categories (not in any group, from admin settings) */}
      {openCategoryCollections.length > 0 && (
        <>
          {groups.length > 0 && <div className="h-px bg-border/30 my-2" />}
          <div className="space-y-1">
            {openCategoryCollections.map((collection) => (
              <label
                key={collection.node.id}
                className={cn(
                  "flex items-center gap-3 w-full text-right px-3 py-2 rounded transition-colors cursor-pointer",
                  isSelected(collection.node.handle)
                    ? "bg-primary/10 text-primary"
                    : "hover:bg-muted text-foreground"
                )}
              >
                <input
                  type="checkbox"
                  checked={isSelected(collection.node.handle)}
                  onChange={() => onToggleCategory(collection.node.handle)}
                  className="accent-primary w-4 h-4"
                />
                <span
                  style={{
                    fontWeight: isSelected(collection.node.handle) ? 400 : 300,
                    fontSize: "14px",
                  }}
                >
                  {collection.node.title}
                </span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default CategoryFilter;
